import { useEffect, useRef, useState } from "react";
import { createNewCategory, updateCategoryById, updateCategoryByCityId, updateScraperCategoryById, createNewCategoryByCityId, getTerminalCategoryScraper } from "../../Services/terminal";
import { get, omit } from "lodash-es";
import { base64ToFile } from "./TerminalForm";
import { validateURL } from "../util/linkValidation";
import { useTranslation } from "react-i18next";
import { FaDownload, FaTimes } from "react-icons/fa";
import axios from "axios";

// A freshly-picked image is a base64 data URL. An unchanged existing image is a
// remote URL string, which must NOT be re-sent as an upload field.
const isNewImage = (img) => typeof img === "string" && img.startsWith("data:");

function dataToFormData(data, { markDeletedImages = false } = {}) {
    const formData = new FormData();

    const {
        image,
        subcategories,
        ...rest
    } = data;

    // In edit mode an emptied image slot means "delete it": send image: null so the
    // backend removes it. New (data:) images ride as file parts below; unchanged
    // remote-URL images are left untouched (field omitted).
    const withDeletionFlag = (obj, img) =>
        markDeletedImages && !img ? { ...obj, image: null } : obj;

    const cleanedSubcategories = subcategories?.map(({ image: subImage, ...subRest }) =>
        withDeletionFlag(subRest, subImage));

    const cleanData = withDeletionFlag({
        ...rest,
        subcategories: cleanedSubcategories,
    }, image);

    formData.append("data", JSON.stringify(cleanData));

    if (image && data.name && isNewImage(image)) {
        formData.append(data.name, base64ToFile(image));
    }

    subcategories.forEach((sub) => {
        if (sub.image && sub.name && isNewImage(sub.image)) {
            formData.append(sub.name, base64ToFile(sub.image));
        }
    });

    return formData;
}

const CreateCategory = ({ scrapers, cityId, isOpen, close, categoryData, isEdit }) => {
    if (!isOpen) return;
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    // const [scrappers, setScrappers] = useState([]);
    // Disabled: edit-mode image add/remove is off (image is read-only in edit). Kept for
    // future re-enable — see commits bcc77c7 (upload in edit) & 4b2ec9e (image: null delete).
    // const originalImages = useRef({ main: null, subcategories: {} });
    const { t } = useTranslation();
    const [newCategory, setNewCategory] = useState({
        name: "",
        description: "",
        website: "",
        isValidLink: true,
        image: null,
        scrapers: [{ link: "", active: true, isValidScrapper: true }],
        subcategories: []
    });
    const handleScraperChange = (index, key, value) => {
        const updated = [...newCategory.scrapers];
        updated[index][key] = value;
        setNewCategory({ ...newCategory, scrapers: updated });
    };

    const addScraper = () => {
        setNewCategory({
            ...newCategory,
            scrapers: [...newCategory.scrapers, { link: "", active: true, isValidScrapper: true }]
        });
    };

    const addSubcategory = () => {
        setNewCategory({
            ...newCategory,
            subcategories: [
                ...newCategory.subcategories,
                {
                    name: "",
                    description: "",
                    website: "",
                    isValidLink: true,
                    image: null,
                    scrapers: [{ link: "", active: true, isValidScrapper: true }],
                }
            ]
        });
    };
    const validateFormData = () => {
        const errorsData = {
            name: '',
            scrapers: '',
            subcategories: []
        };

        if (!newCategory.name.trim() || (!isEdit && !newCategory.image)) {
            errorsData.name = 'Name or image is required';
        }
        if (newCategory?.scrapers?.length > 0) {
            for (let i = 0; i < newCategory?.scrapers?.length; i++) {
                if (!newCategory.scrapers[i]?.link) {
                    errorsData.scrapers = 'Scraper link is required';
                }
                if (!newCategory.scrapers[i]?.isValidScrapper) {
                    errorsData.scrapers = 'Valid url required';
                }
            }
        }
        if (newCategory?.subcategories?.length > 0) {
            for (let i = 0; i < newCategory?.subcategories?.length; i++) {
                if (!newCategory.subcategories[i]?.name || (!isEdit && !newCategory.subcategories[i]?.image)) {
                    errorsData.subcategories.push('Sub categories name or image is required');
                }
                if (!newCategory.subcategories[i]?.isValidLink) {
                    errorsData.subcategories = 'Valid url required';
                }
            }
        }
        return { errorsData, isError: errorsData?.name || errorsData?.subcategories?.length > 0 || errorsData?.scrapers };
    };
    const deleteScrapper = (index) => {
        const updatedScrapers = [...newCategory.scrapers];
        updatedScrapers.splice(index, 1);
        setNewCategory({ ...newCategory, scrapers: updatedScrapers });
    }
    const deleteSubcategories = (index) => {
        const updatedSubcategories = [...newCategory.subcategories];
        updatedSubcategories.splice(index, 1);
        setNewCategory({ ...newCategory, subcategories: updatedSubcategories });
    }
    const handleSubmitCategory = async () => {
        try {
            const { isError } = validateFormData();
            if (!isError) {
                if (isEdit) {
                    const editFormdata = [{ categoryId: newCategory?.id, scrapers: [...newCategory?.scrapers] }, ...newCategory?.subcategories?.filter((subcat) => subcat?.id).map((subcat) => ({
                        categoryId: subcat?.id,
                        scrapers: [...subcat?.scrapers]
                    }))];
                    // Persist category fields + image via the update endpoint (PUT /categories/, id in
                    // body), same FormData shape as create. markDeletedImages sends image: null for an
                    // emptied slot so the backend deletes it. Non-fatal so it never blocks the scraper save.
                    try {
                        await updateCategoryById(newCategory?.id, dataToFormData(omit(newCategory, 'children'), { markDeletedImages: true }));
                    } catch (imgErr) {
                        console.error("Category update failed:", imgErr);
                    }
                    const res = await updateScraperCategoryById(cityId, editFormdata);
                    if (res.data.success === true) {
                        setSuccessMessage(t("category_success"));
                        close(true);
                    }
                } else {
                    // const res = await createNewCategory(dataToFormData(newCategory));
                    const res = await createNewCategoryByCityId(cityId, dataToFormData(newCategory));
                    if (res.data.status === 'success') {
                        setSuccessMessage(t("new_category_sucess"));
                        setNewCategory({
                            name: "",
                            description: "",
                            website: "",
                            isValidLink: true,
                            image: null,
                            scrapers: [{ link: "", active: true, isValidScrapper: true }],
                        })
                        setTimeout(() => {
                            close(true);
                        }, 2000)
                    }
                }
            }
        } catch (e) {
            console.error(e);
            if (axios.isAxiosError(e)) {
                const message = e.response?.data?.message || t("error_category");
                console.error("API Error:", message);
                setErrorMessage(message);
            } else {
                console.error("Unexpected error:", e);
                setErrorMessage(t("error_category"));
            }
        }
        setTimeout(() => {
            setSuccessMessage('');
            setErrorMessage('');
        }, 2000)

    };

    const handleFileChange = (e, changeState) => {
        const file = e.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            changeState(reader.result);
        };
        reader.readAsDataURL(file);
    };
    useEffect(() => {
        if (categoryData) {
            // Disabled with edit-mode image sync (see commits bcc77c7 / 4b2ec9e):
            // originalImages.current = {
            //     main: categoryData?.image || null,
            //     subcategories: (categoryData.children ?? []).reduce((acc, sub) => {
            //         acc[sub.id] = sub?.image || null;
            //         return acc;
            //     }, {}),
            // };
            setNewCategory({
                ...categoryData,
                isValidLink: true,
                scrapers: (scrapers ?? []).reduce((acc, scrp) => {
                    if (scrp?.categoryId == categoryData?.id) {
                        acc.push({ ...scrp, isValidScrapper: true });
                    }
                    return acc;
                }, []).length > 0 ?
                    (scrapers ?? []).reduce((acc, scrp) => {
                        if (scrp?.categoryId == categoryData?.id) {
                            acc.push({ ...scrp, isValidScrapper: true });
                        }
                        return acc;
                    }, []) :
                    [{ link: "", active: true, isValidScrapper: true }]
                ,
                // subcategories: categoryData.children?.map((e) => ({ ...e, isValidLink: true, scrapers: [{ link: e.link, active: e.active, isValidScrapper: e.isValidScrapper }] })) ?? []
                subcategories: categoryData.children?.map((subcat) => ({
                    ...subcat,
                    isValidLink: true,
                    scrapers: (scrapers ?? []).reduce((acc, scrp) => {
                        if (scrp?.categoryId == subcat?.id) {
                            acc.push({ ...scrp, isValidScrapper: true });
                        }
                        return acc;
                    }, []).length > 0 ?
                        (scrapers ?? []).reduce((acc, scrp) => {
                            if (scrp?.categoryId == subcat?.id) {
                                acc.push({ ...scrp, isValidScrapper: true });
                            }
                            return acc;
                        }, []) :
                        [{ link: "", active: true, isValidScrapper: true }]
                })) ?? []
            });
        }
    }, [categoryData, scrapers])
    function validateWebsiteLink(link) {
        if (validateURL(link)) {
            setNewCategory({ ...newCategory, website: link, isValidLink: true })
        } else {
            setNewCategory({ ...newCategory, website: link, isValidLink: false })
        }
    }
    function validateScrapperLink(index, field, value) {
        if (validateURL(value)) {
            const updatedScrapers = [...newCategory.scrapers];
            updatedScrapers[index] = { ...updatedScrapers[index], [field]: value, isValidScrapper: true };
            setNewCategory({ ...newCategory, scrapers: updatedScrapers });
        } else {
            const updatedScrapers = [...newCategory.scrapers];
            updatedScrapers[index] = { ...updatedScrapers[index], [field]: value, isValidScrapper: false };
            setNewCategory({ ...newCategory, scrapers: updatedScrapers });
        }
    }
    return <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{isEdit ? t("categoryUpdate") : t("CategoryCreateNew")} {t("categoryTitle")}</h3>
                <button onClick={() => close()} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>
            <div className="space-y-4">
                <input
                    type="text"
                    placeholder={t("titleLabel")}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                />
                <textarea
                    placeholder={t("descriptionLabel")}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    value={newCategory.description}
                    onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                />
                {/* <input
                    type="url"
                    placeholder="Website Link (Optional)"
                    className="w-full px-4 py-2 border border-gray-300 rounded"
                    value={newCategory?.website}
                    onChange={(e) => validateWebsiteLink(e.target.value)}
                /> */}
                {/* {!newCategory?.isValidLink && (
                    <p className="text-red-500 text-sm mt-1">Please enter a valid URL.</p>
                )} */}
                {newCategory.image ? <div className="relative bg-gray-100 rounded w-auto py-4 px-4 overflow-hidden">
                    {<FaTimes
                        className="absolute top-1 right-1 text-red-600 bg-white rounded-full p-1 cursor-pointer"
                        onClick={(e) => {
                            setNewCategory({ ...newCategory, image: null })
                        }}
                        size={18}
                    />}
                    <a className="text-sm mt-0 underline text-blue-600 flex flex-row gap-2" href={newCategory.image} download target="_blank">{t("download_file")}<FaDownload className="text-lg" />
                    </a>
                    {/* <img
                        src={newCategory.image}
                        alt="Preview"
                        className="object-contain w-full h-full"
                    /> */}
                </div> : <div>
                    {!(isEdit && newCategory.image) && <>
                        <label className="block text-sm font-medium mb-1">
                            {t("upload_category_image")}
                        </label>
                        <input
                            type="file"
                            className="w-full hidden"
                            id={`file-upload-new-categ`}
                            onChange={(e) =>
                                handleFileChange(e, (e) => setNewCategory({ ...newCategory, image: e }))
                            }
                        />
                        <button
                            type="button"
                            className="px-4 py-2 bg-blue-600 text-white rounded"
                            onClick={() => document.getElementById(`file-upload-new-categ`).click()}
                        >
                            {t("uploadMedia")}
                        </button>

                    </>
                    }</div>}

                <div>
                    <h4 className="font-medium mb-2">{t("scrapper")}</h4>
                    {newCategory?.scrapers?.map((scraper, index) => {
                        return (
                            <div>
                                <div key={index} className="flex items-center mb-2 space-x-2">
                                    <input
                                        type="url"
                                        placeholder="Scraper Link *"
                                        value={scraper?.link}
                                        onChange={(e) => validateScrapperLink(index, "link", e.target.value)}
                                        className="flex-1 px-4 py-2 border border-gray-300 rounded"
                                    />
                                    <button onClick={() => deleteScrapper(index)} className="text-gray-400 hover:text-gray-600">✕</button>
                                </div>
                                {!scraper?.isValidScrapper && (
                                    <p className="text-red-500 text-sm mt-1">{t("validurl")}</p>
                                )}
                            </div>
                        )
                    })}
                    <button
                        onClick={addScraper}
                        className="text-sm text-blue-600 hover:underline mt-1"
                    >
                        {t("addanotherlink")}
                    </button>
                </div>

                <div className="mt-6">
                    <h4 className="font-medium mb-2">{t("subCategories")}</h4>
                    {newCategory?.subcategories?.map((subcat, i) => (
                        <div key={i} className="mb-4 p-4 border rounded bg-gray-50">
                            <div className="w-full flex justify-end">
                                <button onClick={() => deleteSubcategories(i)} className="text-gray-400 hover:text-gray-600">✕</button>
                            </div>
                            <input
                                type="text"
                                placeholder={t("subCategories") + " " + t("titleLabel")}
                                className="w-full mb-2 px-4 py-2 border border-gray-300 rounded"
                                value={subcat.name}
                                onChange={(e) => {
                                    const updated = [...newCategory.subcategories];
                                    updated[i].name = e.target.value;
                                    setNewCategory({ ...newCategory, subcategories: updated });
                                }}
                            />
                            <textarea
                                placeholder={t("subCategories") + " " + t("descriptionLabel")}
                                className="w-full mb-2 px-4 py-2 border border-gray-300 rounded"
                                value={subcat.description}
                                onChange={(e) => {
                                    const updated = [...newCategory.subcategories];
                                    updated[i].description = e.target.value;
                                    setNewCategory({ ...newCategory, subcategories: updated });
                                }}
                            />
                            {/* <input
                                type="url"
                                placeholder={t("subCategories") + " " +t("webLink")}
                                className="w-full mb-2 px-4 py-2 border border-gray-300 rounded"
                                value={subcat.website}
                                onChange={(e) => {
                                    const updated = [...newCategory.subcategories];
                                    updated[i].website = e.target.value;
                                    if (validateURL(e.target.value)) {
                                        updated[i].isValidLink = true
                                        setNewCategory({ ...newCategory, subcategories: updated });
                                    } else {
                                        updated[i].isValidLink = false
                                        setNewCategory({ ...newCategory, subcategories: updated });
                                    }
                                }}
                            />
                            {!subcat.isValidLink && (
                                <p className="text-red-500 text-sm mt-1">Please enter a valid URL.</p>
                            )} */}
                            {subcat.image ? <div className="relative bg-gray-100 rounded w-auto py-4 px-4 overflow-hidden">
                                {<FaTimes
                                    className="absolute top-1 right-1 text-red-600 bg-white rounded-full p-1 cursor-pointer"
                                    onClick={(e) => {
                                        const updated = [...newCategory.subcategories];
                                        updated[i].image = null;
                                        setNewCategory({ ...newCategory, subcategories: updated })

                                    }}
                                    size={18}
                                />}
                                <a className="text-sm mt-0 underline text-blue-600 flex flex-row gap-2" href={subcat.image} download target="_blank">{t("download_file")}<FaDownload className="text-lg" />
                                </a>
                                {/* <img
                                    src={subcat.image}
                                    alt="Preview"
                                    className="object-contain w-full h-full"
                                /> */}
                            </div> :
                                <div>
                                    {!(isEdit && newCategory.subcategories[i].image) &&
                                        <>
                                            <label className="block text-sm font-medium mb-1">
                                                {t("upload_sub_category_image")}
                                            </label>
                                            <input
                                                type="file"
                                                className="w-full mb-2 hidden"
                                                id={`file-upload-${i}`}
                                                onChange={(e) => {
                                                    handleFileChange(e, (ev) => {
                                                        const updated = [...newCategory.subcategories];
                                                        updated[i].image = ev;
                                                        setNewCategory({ ...newCategory, subcategories: updated })
                                                    })
                                                }
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="px-4 py-2 bg-blue-600 text-white rounded"
                                                onClick={() => document.getElementById(`file-upload-${i}`).click()}
                                            >
                                                {t("uploadMedia")}
                                            </button>

                                        </>
                                    }</div>}
                            <div>
                                <h5 className="font-semibold mb-1">{t("subCategoryLabel")}</h5>
                                {subcat.scrapers?.map((scr, si) => (
                                    <div>
                                        <div key={si} className="flex items-center mb-2 space-x-2">
                                            <input
                                                type="url"
                                                placeholder="Link"
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded"
                                                value={scr?.link}
                                                onChange={(e) => {
                                                    const updated = [...newCategory.subcategories];
                                                    updated[i].scrapers[si].link = e.target.value;
                                                    if (validateURL(e.target.value)) {
                                                        updated[i].scrapers[si].isValidScrapper = true
                                                        setNewCategory({ ...newCategory, subcategories: updated });
                                                    } else {
                                                        updated[i].scrapers[si].isValidScrapper = false
                                                        setNewCategory({ ...newCategory, subcategories: updated });
                                                    }
                                                }}
                                            />
                                        </div>
                                        {!scr.isValidScrapper && (
                                            <p className="text-red-500 text-sm mt-1">{t("validurl")}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={addSubcategory}
                        className="text-sm text-blue-600 hover:underline mt-1"
                    >
                        {t("addSubCategoryLabel")}
                    </button>
                </div>
                {successMessage && <p className="text-green-600 text-bold">{successMessage}</p>}
                {errorMessage && <p className="text-red-600 text-bold">{errorMessage}</p>}
                <button
                    onClick={handleSubmitCategory}
                    className="text-sm py-2 px-4 rounded bg-blue-600 text-white hover:underline mt-1 disabled:bg-gray-100 disabled:text-gray-400 disabled:font-black disabled:cursor-not-allowed"
                    disabled={validateFormData().isError || errorMessage}
                >
                    {t("submitLabel")}
                </button>
            </div>
        </div>
    </div>
}

export default CreateCategory;
